<template>
  <div>
    <hr/>

    <b-row class="mt-15 align-items-center">
      <b-col cols="12">
        <div class="fs-dbb-20">
          {{ $t("engine.managePage.pageAddEdit.permission.password.lblPassword", languageEngine) }}
        </div>
      </b-col>
    </b-row>

    <!-- Form -->
    <b-row class="mt-15 align-items-baseline">
      <b-col cols="4">
        <label for="">
          {{ $t("engine.managePage.pageAddEdit.permission.password.lblSetPassword", languageEngine) }}
          <span class="text-hint">({{ languageUse && languageUse.toUpperCase() }})</span>
        </label>
        <span 
          class="text-hint cursor-pointer ml-3"
          :title="$t('engine.managePage.pageAddEdit.permission.password.lblTooltip', languageEngine)"
          v-b-tooltip.html.hover
        >
          <i class="fas fa-question-circle"></i>
        </span>
        <span class="text-danger ml-3">*</span>
      </b-col>
      <b-col cols="8" :class="{ errors: validatePassword.$error }">
        <div class="position-relative">
          <b-form-input
            :type="showPassword ? 'text' : 'password'"
            v-model="password"
            :placeholder="$t('engine.managePage.pageAddEdit.permission.password.plcPassword', languageEngine)"
          />
          <span class="text-hint position-absolute cursor-pointer" style="line-height: 1.2; top: 8px; right: 12px;" v-show="!showPassword" @click="showPassword = true">
            <i class="far fa-eye fs-12"></i>
          </span>
          <span class="text-hint position-absolute cursor-pointer" style="line-height: 1.2; top: 8px; right: 12px;" v-show="showPassword" @click="showPassword = false">
            <i class="far fa-eye-slash fs-12"></i>
          </span>

          <small data-validate="required" v-if="!validatePassword.required && validatePassword.$error">{{ $t("engine.managePage.pageAddEdit.permission.password.errPassword", languageEngine) }}</small>
          <small data-validate="maxLength" v-else-if="!validatePassword.maxLength && validatePassword.$error">{{ $t("engine.managePage.pageAddEdit.permission.password.errPasswordLength", languageEngine) }}</small>
          <small data-validate="format" v-else-if="!validatePassword.format && validatePassword.$error">{{ $t("engine.managePage.pageAddEdit.permission.password.errPasswordFormat", languageEngine) }}</small>
        </div>
      </b-col>
    </b-row>

    <b-row class="mt-15 align-items-baseline">
      <b-col cols="4">
        <label for="passwordPageTitle">
          {{ $t("engine.managePage.pageAddEdit.permission.password.lblTitle", languageEngine) }}
          <span class="text-hint">({{ languageUse && languageUse.toUpperCase() }})</span>
        </label>
      </b-col>
      <b-col cols="8">
        <b-form-input
          name="passwordPageTitle"
          v-model="passwordPageTitle"
          :placeholder="$t('engine.managePage.pageAddEdit.permission.password.plcTitle', languageEngine)"
        />
      </b-col>
    </b-row>

    <b-row class="mt-15 align-items-baseline">
      <b-col cols="4">
        <label for="passwordPageSubtitle">
          {{ $t("engine.managePage.pageAddEdit.permission.password.lblSubTitle", languageEngine) }}
          <span class="text-hint">({{ languageUse && languageUse.toUpperCase() }})</span>
        </label>
      </b-col>
      <b-col cols="8">
        <b-form-input
          name="passwordPageSubtitle"
          v-model="passwordPageSubtitle"
          :placeholder="$t('engine.managePage.pageAddEdit.permission.password.plcSubTitle', languageEngine)"
        />
      </b-col>
    </b-row>

    <b-row class="mt-15 align-items-baseline">
      <b-col cols="4">
        <label for="passwordPageButton">
          {{ $t("engine.managePage.pageAddEdit.permission.password.lblBtnText", languageEngine) }}
          <span class="text-hint">({{ languageUse && languageUse.toUpperCase() }})</span>
        </label>
      </b-col>
      <b-col cols="8">
        <b-form-input
          name="passwordPageButton"
          v-model="passwordPageButton"
          :placeholder="$t('engine.managePage.pageAddEdit.permission.password.plcBtnText', languageEngine)"
        />
      </b-col>
    </b-row>

    <!-- Preview -->
    <hr/>
    <b-row>
      <b-col cols="12">
        <div>
          <span class="fs-dbb-20">{{ $t("engine.managePage.pageAddEdit.permission.password.preview.lblPreview", languageEngine) }}</span>
          <span class="fs-db-20 text-hint ml-3">({{ languageUse && languageUse.toUpperCase() }})</span>
        </div>
      </b-col>
    </b-row>
    <b-row class="mt-5">
      <b-col cols="12">
        <PermissionPasswordPreview :data="data" />
      </b-col>
    </b-row>
    <ModalConfirmUpgrade 
      :modalID="'modal-confirm-upgrade-passward'"
      @clear="$emit('clear')"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { capitalize } from "@helpers/text.helper";
import PermissionPasswordPreview from "./PermissionPasswordPreview.vue";
import pageService from "@services/pageService.js";
import ModalConfirmUpgrade from "@components_system/Design/Modal/ModalConfirmUpgrade/ModalConfirmUpgrade.vue";

export default {
  components: {
    PermissionPasswordPreview,
    ModalConfirmUpgrade,
  },
  props: {
    data: {
      type: Object,
      default() {
        return {};
      },
    },
    validate: {},
  },
  data() {
    return {
      types: ["public", "password", "member"],
      groupType: [],
      showPassword: false,
      isHover: "",
    };
  },
  computed: {
    ...mapGetters({
      languageUse: "design/site/languageUse",
      languageEngine: "design/userPreference/languageEngine",
      permissions: "design/permissionWebsite/permissions",
    }),
    password: {
      get() {
        return this.validate.data.properties.permission.password.$model;
      },
      set(val) {
        this.validate.data.properties.permission.password.$model = val;
      }
    },
    passwordPageTitle: {
      get() {
        return this.data.properties.permission.passwordPageTitle;
      },
      set(val) {
        this.data.properties.permission.passwordPageTitle = val;
      }
    },
    passwordPageSubtitle: {
      get() {
        return this.data.properties.permission.passwordPageSubtitle;
      },
      set(val) {
        this.data.properties.permission.passwordPageSubtitle = val;
      }
    },
    passwordPageButton: {
      get() {
        return this.data.properties.permission.passwordPageButton;
      },
      set(val) {
        this.data.properties.permission.passwordPageButton = val;
      }
    },
    validatePassword() {
      return this.validate.data.properties.permission.password;
    }
  },
  methods: {
    capitalize,
    getMemberGroup() {
      const language = this.languageUse;
      pageService.memberGroup({ language }).then((response) => {
        this.groupType = response.data;
      });
    },
  },
  mounted() {
    if(this.permissions.isPagePermission == 0) {
      this.$bvModal.show('modal-confirm-upgrade-passward');
    }
  },
};
</script>

<style lang="scss">
.type-permission.active {
  color: #212529 !important;
  /* background-color: #fff !important; */

  svg {
    border-radius: 8px;
  }
}
#setting-page-permission {
  .custom-control.custom-checkbox label {
    line-height: 20px;
  }
  input[type="password"]::-ms-reveal,
  input[type="password"]::-ms-clear {
    display: none;
  }
}
</style>
